// /frontend/src/pages/dashboard/post/threads/ThreadsPostService.js
import axios from 'axios';

/**
 * publishThreadsPost
 * @param {string} threadsId   - The user/account ID from your DB (e.g. '123456789' or whatever you store)
 * @param {string} message     - The text to post
 * @param {string} [mediaUrl]  - (Optional) Public URL for an image/video on S3
 * @returns {Promise<Object>}  - The server's JSON response, e.g. { success: true, postId, ... }
 */
export async function publishThreadsPost(threadsId, message, mediaUrl) {
  try {
    // Validation
    if (!threadsId) {
      throw new Error('Threads account ID is required');
    }
    if (!message && !mediaUrl) {
      throw new Error('You must provide either text or media for your Thread');
    }
    
    const response = await axios.post(
      '/api/posting/threads/publish',
      {
        threadsId,
        message,
        mediaUrl, // can be null if no media
      },
      {
        withCredentials: true, // important for session-based auth
      }
    );
    
    console.log('publishThreadsPost success:', response.data);
    return response.data; // e.g. { success: true, threads_post_id: '...', message: '...' }
    
  } catch (err) {
    console.error('publishThreadsPost error:', err);
    
    // Extract the actual error message from various possible locations
    let errorMessage = 'Failed to post to Threads';
    
    // If this is our custom validation error, preserve it
    if (!err.response && err.message) {
      errorMessage = err.message;
    } else if (err.response?.data) {
      // Check multiple possible error locations in order of preference
      if (err.response.data.error && typeof err.response.data.error === 'string') {
        errorMessage = err.response.data.error;
      } else if (err.response.data.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.response.data.details) {
        errorMessage = err.response.data.details;
      } else if (err.response.data.error?.error_user_msg) {
        // Threads uses Meta/Facebook API format
        errorMessage = err.response.data.error.error_user_msg;
      } else if (err.response.data.error?.error_message) {
        // Another Meta/Facebook API error format
        errorMessage = err.response.data.error.error_message;
      } else if (err.response.data.error?.code) {
        // Threads/Meta specific error codes
        errorMessage = getThreadsErrorByCode(err.response.data.error.code, err.response.data.error.message);
      } else if (err.response.data.error_description) {
        // OAuth error format
        errorMessage = err.response.data.error_description;
      } else if (typeof err.response.data === 'string') {
        errorMessage = err.response.data;
      }
    } else if (err.response?.status) {
      // Handle HTTP status codes
      errorMessage = getThreadsErrorByStatus(err.response.status, err.message);
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    // Throw error with the extracted message
    throw new Error(errorMessage);
  }
}

/**
 * Maps Threads/Meta error codes to user-friendly messages
 */
function getThreadsErrorByCode(code, fallbackMessage) {
  const errorMap = {
    1: 'Threads API error. Please try again.',
    2: 'Threads service temporarily unavailable. Please try again later.',
    4: 'Threads rate limit exceeded. Please wait before posting again.',
    10: 'Threads permission denied. Please reconnect with proper permissions.',
    17: 'Threads user request limit reached. Please try again later.',
    32: 'Threads page request limit reached. Please wait before posting.',
    100: 'Invalid Threads parameters. Please check your content.',
    190: 'Threads access token expired. Please reconnect your account.',
    200: 'Threads permission required. Please reconnect your account.',
    201: 'Threads permission required. Please reconnect your account.',
    202: 'Threads permission required. Please reconnect your account.',
    341: 'Threads daily posting limit reached. Try again tomorrow.',
    368: 'Threads temporarily blocked this action. Please try again later.',
    506: 'Duplicate Thread. This content was already posted.',
    2207006: 'Threads account not properly connected. Please reconnect.',
    2207051: 'Threads Business account required for this action.',
    36000: 'Threads media upload failed. Please check your image/video.',
    36001: 'Threads media format not supported. Use JPG, PNG, or MP4.',
    36002: 'Threads media file too large. Maximum 8MB for images, 1GB for videos.',
    36003: 'Threads video duration exceeded. Maximum 5 minutes allowed.',
  };
  
  return errorMap[code] || fallbackMessage || `Threads error (code: ${code})`;
}

/**
 * Maps HTTP status codes to user-friendly messages
 */
function getThreadsErrorByStatus(status, fallbackMessage) {
  const statusMap = {
    400: 'Invalid Threads request. Please check your content format.',
    401: 'Threads authentication failed. Please reconnect your account.',
    403: 'Threads permission denied. Check your account permissions.',
    404: 'Threads account not found. Please reconnect.',
    429: 'Too many Threads requests. Please slow down and try again.',
    500: 'Threads server error. Please try again later.',
    502: 'Threads server error. Please try again later.',
    503: 'Threads service unavailable. Please try again later.',
  };
  
  return statusMap[status] || fallbackMessage || `Threads error (status: ${status})`;
}

/**
 * Helper function to get user-friendly error messages
 * Can be used by UI components to show better error dialogs
 */
export function getThreadsErrorMessage(error) {
  const message = error.message || error.toString();
  const lowerMessage = message.toLowerCase();
  
  // Map technical errors to user-friendly messages with actions
  if (lowerMessage.includes('token expired') || lowerMessage.includes('authentication failed')) {
    return {
      title: 'Connection Expired',
      message: 'Your Threads connection has expired. Please reconnect your account in Settings.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('not properly connected')) {
    return {
      title: 'Connection Issue',
      message: 'Threads account not properly connected. Please reconnect in Settings.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('permission')) {
    return {
      title: 'Permission Required',
      message: 'Missing Threads permissions. Please reconnect with all required permissions.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('business account required')) {
    return {
      title: 'Business Account Required',
      message: 'This feature requires a Threads Business account. Please upgrade your account.',
      action: 'upgrade'
    };
  }
  
  if (lowerMessage.includes('rate limit')) {
    return {
      title: 'Slow Down',
      message: 'You\'re posting too quickly. Please wait a few minutes before trying again.',
      action: 'wait'
    };
  }
  
  if (lowerMessage.includes('daily posting limit')) {
    return {
      title: 'Daily Limit Reached',
      message: 'You\'ve reached your daily Threads posting limit. Try again tomorrow.',
      action: 'wait_daily'
    };
  }
  
  if (lowerMessage.includes('temporarily blocked')) {
    return {
      title: 'Temporarily Blocked',
      message: 'Threads has temporarily blocked this action. Please try again in a few hours.',
      action: 'wait'
    };
  }
  
  if (lowerMessage.includes('duplicate')) {
    return {
      title: 'Duplicate Content',
      message: 'This content was already posted to Threads.',
      action: null
    };
  }
  
  if (lowerMessage.includes('file too large')) {
    return {
      title: 'File Too Large',
      message: 'Your media file is too large. Maximum 8MB for images, 1GB for videos.',
      action: 'resize'
    };
  }
  
  if (lowerMessage.includes('format not supported')) {
    return {
      title: 'Invalid Format',
      message: 'Threads doesn\'t support this file format. Use JPG, PNG, or MP4.',
      action: 'convert'
    };
  }
  
  if (lowerMessage.includes('video duration')) {
    return {
      title: 'Video Too Long',
      message: 'Your video is too long for Threads. Maximum duration is 5 minutes.',
      action: 'trim'
    };
  }
  
  if (lowerMessage.includes('media upload failed')) {
    return {
      title: 'Upload Failed',
      message: 'Failed to upload your media. Please check the file and try again.',
      action: 'retry'
    };
  }
  
  // Default
  return {
    title: 'Posting Failed',
    message: message,
    action: null
  };
}